import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import ResponsiveDialog from '../common/ResponsiveDialog';
import HandoverConfirmDialog from './HandoverConfirmDialog';
import pettyCashApi from '../../services/pettyCashApi';
import { showSnackbar } from '../../store/slices/uiSlice';
import type { HandoverConfirmInput, HandoverList, HandoverView } from '../../types/pettyCash';
import { formatAmount, formatDate } from '../../utils/pettyCash';

/** Petty Cash Setup › Handovers: every handover of the company, its two confirmations, and cancelling a pending one. */

interface Props {
  companyId: number;
  currency: string;
  /** Changes when a handover was started elsewhere on the page (Holders tab). */
  reloadKey: number;
  /** A handover completed or was cancelled: the holders change. */
  onChanged: () => void;
}

const STATUS_COLOR: Record<HandoverView['status'], 'warning' | 'success' | 'default'> = { Pending: 'warning', Completed: 'success', Cancelled: 'default' };
const shown = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatAmount(n));

const HandoversTab: React.FC<Props> = ({ companyId, currency, reloadKey, onChanged }) => {
  const dispatch = useDispatch();
  const [list, setList] = useState<HandoverList | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The dialogs keep their handover while they close.
  const [cancelling, setCancelling] = useState<{ open: boolean; view: HandoverView | null }>({ open: false, view: null });
  const [reason, setReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<{ open: boolean; view: HandoverView | null; side: 'from' | 'to' }>({ open: false, view: null, side: 'from' });

  const load = useCallback(async () => {
    try {
      setList(await pettyCashApi.handovers(companyId));
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'The handovers could not be loaded');
    }
  }, [companyId]);

  useEffect(() => { void load(); }, [load, reloadKey]);

  const cancel = async () => {
    if (!cancelling.view || !reason.trim()) return;
    setCancelBusy(true);
    setCancelError(null);
    try {
      await pettyCashApi.cancelHandover(cancelling.view.handoverId, reason.trim());
      setCancelling((c) => ({ ...c, open: false }));
      dispatch(showSnackbar({ message: 'Handover cancelled', severity: 'success' }));
      await load();
      onChanged();
    } catch (e: any) {
      setCancelError(e?.response?.data?.message || e?.message || 'The handover could not be cancelled');
    } finally {
      setCancelBusy(false);
    }
  };

  const confirm = async (handoverId: number, input: HandoverConfirmInput) => {
    const view = await pettyCashApi.confirmHandover(handoverId, input);
    setConfirming((c) => ({ ...c, open: false }));
    dispatch(showSnackbar({ message: view.status === 'Completed' ? 'Handover completed' : 'Handover confirmed', severity: 'success' }));
    await load();
    onChanged();
  };

  const columns: ResponsiveTableColumn<HandoverView>[] = [
    {
      id: 'started',
      header: 'Started',
      cell: (h) => (
        <Box>
          <Typography variant="body2">{formatDate(h.createdAt)}</Typography>
          {h.createdByName ? <Typography variant="caption" color="text.secondary">by {h.createdByName}</Typography> : null}
        </Box>
      ),
    },
    {
      id: 'who',
      header: 'From → to',
      cell: (h) => <Typography variant="body2" fontWeight={600}>{h.from.name} → {h.to.name}</Typography>,
    },
    {
      id: 'account',
      header: 'SAP cash account',
      hideOnMobile: true,
      cell: (h) => (
        <Box>
          <Typography variant="body2">{h.sapAccount} {h.sapAccountName || ''}</Typography>
          {h.auxiliaryCode ? <Typography variant="caption" color="text.secondary">Auxiliary {h.auxiliaryCode} {h.auxiliaryName || ''}</Typography> : null}
        </Box>
      ),
    },
    {
      id: 'figures',
      header: `Float / counted ${currency}`.trim(),
      align: 'right',
      cell: (h) => (
        <Box>
          <Typography variant="body2">{shown(h.floatAmount)} / {shown(h.cashCounted)}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Not yet reimbursed {shown(h.outstanding)}{h.advances ? ` · advances ${formatAmount(h.advances)}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'confirmations',
      header: 'Confirmations',
      cell: (h) => (
        <Stack spacing={0.25}>
          <Typography variant="caption">{h.from.name}: {h.fromConfirmedAt ? `counted ${formatDate(h.fromConfirmedAt)}` : 'not yet'}</Typography>
          <Typography variant="caption">{h.to.name}: {h.toConfirmedAt ? `received ${formatDate(h.toConfirmedAt)}` : h.fromConfirmedAt ? 'not yet' : 'after the count'}</Typography>
        </Stack>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (h) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <Chip size="small" color={STATUS_COLOR[h.status] || 'default'} label={h.status} />
          {h.status === 'Completed' && h.completedAt ? <Typography variant="caption" color="text.secondary">{formatDate(h.completedAt)}</Typography> : null}
          {h.status === 'Cancelled' && h.cancelReason ? <Typography variant="caption" color="text.secondary">{h.cancelReason}</Typography> : null}
        </Stack>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (h) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          {h.canConfirmFrom || h.canConfirmTo ? (
            <Button size="small" onClick={() => setConfirming({ open: true, view: h, side: h.canConfirmFrom ? 'from' : 'to' })}>Confirm</Button>
          ) : null}
          {h.canCancel ? (
            <Button size="small" variant="outlined" color="error" onClick={() => { setCancelling({ open: true, view: h }); setReason(''); setCancelError(null); }}>Cancel</Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A handover starts from the Holders tab (Hand over). The outgoing holder counts the cash and confirms; the successor then confirms receiving it,
        which completes it.
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      {!list && !error ? (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
      ) : list ? (
        <ResponsiveTable ariaLabel="Petty cash handovers" columns={columns} rows={list.handovers} getRowKey={(h) => h.handoverId} emptyMessage="No handovers yet" />
      ) : null}

      <ResponsiveDialog
        open={cancelling.open}
        onClose={cancelBusy ? undefined : () => setCancelling((c) => ({ ...c, open: false }))}
        maxWidth="xs"
        fullWidth
        mobileFullScreen={false}
        aria-labelledby="pc-handover-cancel-title"
      >
        <DialogTitle id="pc-handover-cancel-title">Cancel the handover?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {cancelling.view ? `${cancelling.view.from.name} stays the holder; nothing changes on the holders.` : ''}
            </Typography>
            <TextField id="pc-handover-cancel-reason" label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value.slice(0, 1000))} multiline minRows={2} fullWidth required />
            {cancelError ? <Alert severity="error">{cancelError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCancelling((c) => ({ ...c, open: false }))} disabled={cancelBusy}>Back</Button>
          <Button color="error" onClick={() => { void cancel(); }} disabled={cancelBusy || !reason.trim()}>{cancelBusy ? 'Cancelling…' : 'Cancel the handover'}</Button>
        </DialogActions>
      </ResponsiveDialog>

      <HandoverConfirmDialog
        open={confirming.open}
        handover={confirming.view}
        side={confirming.side}
        currency={currency}
        onClose={() => setConfirming((c) => ({ ...c, open: false }))}
        onConfirm={confirm}
      />
    </Box>
  );
};

export default HandoversTab;
